import React from 'react';
import { adminService } from '../../services/adminService';
import { EmailReplyTemplateScenario, EmailReplyTemplateTone, GoldReplyExample } from '../../types';

type Scenario = EmailReplyTemplateScenario;
type Tone = EmailReplyTemplateTone;

const scenarioOptions: Scenario[] = [
  'ANSWER_AND_GUIDE',
  'ASK_MISSING_INFO',
  'ACKNOWLEDGE_AND_REVIEW',
  'SEND_OFFER',
  'FOLLOW_UP',
  'VERIFY_CLIENT',
  'CONFIRM_NEXT_STEP',
  'CUSTOM_REPLY',
];

const toneOptions: Tone[] = ['PROFESSIONAL', 'PREMIUM', 'FRIENDLY', 'URGENT', 'NEUTRAL'];

const scenarioLabel = (value: string) => String(value || '').replace(/_/g, ' ');

const newDraft = (): Omit<GoldReplyExample, 'id' | 'createdAt' | 'updatedAt'> => ({
  module: 'EMAIL',
  scenario: 'ASK_MISSING_INFO',
  title: '',
  inputSummary: '',
  approvedSubject: '',
  approvedBody: '',
  tone: 'PROFESSIONAL',
  tags: [],
  active: true,
  approvedByUserId: null,
  approvalNote: '',
  sourceSuggestionLogId: '',
});

export const GoldExamplesManager: React.FC = () => {
  const [items, setItems] = React.useState<GoldReplyExample[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [scenarioFilter, setScenarioFilter] = React.useState<'ALL' | Scenario>('ALL');
  const [activeFilter, setActiveFilter] = React.useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [search, setSearch] = React.useState('');
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [promoteOpen, setPromoteOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<Omit<GoldReplyExample, 'id' | 'createdAt' | 'updatedAt'>>(newDraft());
  const [tagsText, setTagsText] = React.useState('');
  const [promoteDraft, setPromoteDraft] = React.useState({
    sourceSuggestionLogId: '',
    title: '',
    scenario: '' as '' | Scenario,
    inputSummary: '',
    approvedSubject: '',
    approvedBody: '',
    tone: 'PROFESSIONAL' as Tone,
    tagsText: '',
    approvalNote: '',
    active: true,
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const next = await adminService.listEmailGoldExamples({
        scenario: scenarioFilter === 'ALL' ? undefined : scenarioFilter,
        active: activeFilter === 'ALL' ? undefined : activeFilter === 'ACTIVE',
        search: search.trim() || undefined,
        limit: 500,
      });
      setItems(next);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load gold examples');
    } finally {
      setLoading(false);
    }
  }, [scenarioFilter, activeFilter, search]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const resetEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
    setDraft(newDraft());
    setTagsText('');
  };

  const openCreate = () => {
    setEditingId(null);
    setDraft(newDraft());
    setTagsText('');
    setEditorOpen(true);
  };

  const openEdit = (item: GoldReplyExample) => {
    setEditingId(item.id);
    setDraft({
      module: 'EMAIL',
      scenario: item.scenario,
      title: item.title || '',
      inputSummary: item.inputSummary || '',
      approvedSubject: item.approvedSubject || '',
      approvedBody: item.approvedBody || '',
      tone: (item.tone as Tone) || 'PROFESSIONAL',
      tags: Array.isArray(item.tags) ? item.tags : [],
      active: item.active !== false,
      approvedByUserId: item.approvedByUserId || null,
      approvalNote: item.approvalNote || '',
      sourceSuggestionLogId: item.sourceSuggestionLogId || '',
    });
    setTagsText(Array.isArray(item.tags) ? item.tags.join(', ') : '');
    setEditorOpen(true);
  };

  const saveDraft = async () => {
    const title = String(draft.title || '').trim();
    const body = String(draft.approvedBody || '').trim();
    const summary = String(draft.inputSummary || '').trim();
    if (!title || !body || !summary) {
      setError('Title, Input Summary, and Approved Body are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...draft,
        module: 'EMAIL' as const,
        title,
        inputSummary: summary,
        approvedSubject: String(draft.approvedSubject || '').trim() || undefined,
        approvedBody: body,
        approvalNote: String(draft.approvalNote || '').trim() || undefined,
        sourceSuggestionLogId: String(draft.sourceSuggestionLogId || '').trim() || undefined,
        tags: tagsText
          .split(',')
          .map(value => value.trim())
          .filter(Boolean),
      };
      if (editingId) {
        await adminService.updateEmailGoldExample(editingId, payload as any);
      } else {
        await adminService.createEmailGoldExample(payload as any);
      }
      resetEditor();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save gold example');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item: GoldReplyExample) => {
    try {
      await adminService.toggleEmailGoldExample(item.id, !item.active);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to toggle example');
    }
  };

  const softDelete = async (item: GoldReplyExample) => {
    const ok = window.confirm(`Deactivate "${item.title}"?`);
    if (!ok) return;
    try {
      await adminService.deleteEmailGoldExample(item.id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete example');
    }
  };

  const submitPromote = async () => {
    if (!promoteDraft.title.trim()) {
      setError('Promotion title is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await adminService.promoteEmailGoldExample({
        sourceSuggestionLogId: promoteDraft.sourceSuggestionLogId.trim() || undefined,
        title: promoteDraft.title.trim(),
        scenario: promoteDraft.scenario || undefined,
        inputSummary: promoteDraft.inputSummary.trim() || undefined,
        approvedSubject: promoteDraft.approvedSubject.trim() || undefined,
        approvedBody: promoteDraft.approvedBody.trim() || undefined,
        tone: promoteDraft.tone,
        tags: promoteDraft.tagsText.split(',').map(v => v.trim()).filter(Boolean),
        approvalNote: promoteDraft.approvalNote.trim() || undefined,
        active: promoteDraft.active,
      });
      setPromoteOpen(false);
      setPromoteDraft({
        sourceSuggestionLogId: '',
        title: '',
        scenario: '',
        inputSummary: '',
        approvedSubject: '',
        approvedBody: '',
        tone: 'PROFESSIONAL',
        tagsText: '',
        approvalNote: '',
        active: true,
      });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to promote example');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Gold Examples</p>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Supervisor-approved real replies used as high-quality reference assets for consistent Email suggestions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPromoteOpen(true)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600"
          >
            Promote From Log
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="rounded-xl bg-slate-900 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white"
          >
            Create Example
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
        <select
          value={scenarioFilter}
          onChange={(event) => setScenarioFilter(event.target.value as 'ALL' | Scenario)}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700"
        >
          <option value="ALL">All Scenarios</option>
          {scenarioOptions.map(option => (
            <option key={option} value={option}>{scenarioLabel(option)}</option>
          ))}
        </select>
        <select
          value={activeFilter}
          onChange={(event) => setActiveFilter(event.target.value as 'ALL' | 'ACTIVE' | 'INACTIVE')}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700"
        >
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search title/body/summary..."
          className="md:col-span-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 outline-none"
        />
      </div>

      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-2">
        {loading ? <div className="text-xs text-slate-400">Loading examples…</div> : null}
        {!loading && items.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
            No gold examples found.
          </div>
        ) : null}
        {items.map(item => (
          <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-black text-slate-800">{item.title}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  {scenarioLabel(item.scenario)} · {item.tone || 'NEUTRAL'}
                </p>
                <p className="mt-1 text-[11px] text-slate-500">{item.inputSummary}</p>
                {Array.isArray(item.tags) && item.tags.length ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {item.tags.slice(0, 8).map(tag => (
                      <span key={`${item.id}-${tag}`} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-500">
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleActive(item)}
                  className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest ${
                    item.active ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500'
                  }`}
                >
                  {item.active ? 'Active' : 'Inactive'}
                </button>
                <button
                  type="button"
                  onClick={() => openEdit(item)}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-600"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => softDelete(item)}
                  className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-700"
                >
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editorOpen ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 p-4">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-auto rounded-2xl border border-slate-200 bg-white p-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h4 className="text-base font-black text-slate-900">{editingId ? 'Edit Gold Example' : 'Create Gold Example'}</h4>
                <p className="text-[12px] text-slate-500">Examples are reference assets for better suggestions. They never auto-send.</p>
              </div>
              <button type="button" onClick={resetEditor} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-slate-500">X</button>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <label className="space-y-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Title</span>
                <input value={draft.title || ''} onChange={(event) => setDraft({ ...draft, title: event.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Scenario</span>
                <select value={draft.scenario} onChange={(event) => setDraft({ ...draft, scenario: event.target.value as Scenario })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
                  {scenarioOptions.map(option => (
                    <option key={option} value={option}>{scenarioLabel(option)}</option>
                  ))}
                </select>
              </label>

              <label className="space-y-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Tone</span>
                <select value={(draft.tone as Tone) || 'PROFESSIONAL'} onChange={(event) => setDraft({ ...draft, tone: event.target.value as Tone })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
                  {toneOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Tags (comma separated)</span>
                <input value={tagsText} onChange={(event) => setTagsText(event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>

              <label className="space-y-1 lg:col-span-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Input Summary</span>
                <textarea value={draft.inputSummary || ''} onChange={(event) => setDraft({ ...draft, inputSummary: event.target.value })} rows={3} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>

              <label className="space-y-1 lg:col-span-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Approved Subject</span>
                <input value={draft.approvedSubject || ''} onChange={(event) => setDraft({ ...draft, approvedSubject: event.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>

              <label className="space-y-1 lg:col-span-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Approved Body</span>
                <textarea value={draft.approvedBody || ''} onChange={(event) => setDraft({ ...draft, approvedBody: event.target.value })} rows={8} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>

              <label className="space-y-1 lg:col-span-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Approval Note</span>
                <textarea value={draft.approvalNote || ''} onChange={(event) => setDraft({ ...draft, approvalNote: event.target.value })} rows={2} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>

              <label className="space-y-1 lg:col-span-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Source Suggestion Log ID (optional)</span>
                <input value={draft.sourceSuggestionLogId || ''} onChange={(event) => setDraft({ ...draft, sourceSuggestionLogId: event.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </label>

              <button
                type="button"
                onClick={() => setDraft({ ...draft, active: !draft.active })}
                className={`w-fit rounded-xl border px-3 py-2 text-[10px] font-black uppercase tracking-widest ${
                  draft.active ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500'
                }`}
              >
                {draft.active ? 'Active' : 'Inactive'}
              </button>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button type="button" onClick={resetEditor} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600">Cancel</button>
              <button type="button" onClick={() => void saveDraft()} disabled={saving} className="rounded-lg bg-slate-900 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-60">{saving ? 'Saving...' : 'Save Example'}</button>
            </div>
          </div>
        </div>
      ) : null}

      {promoteOpen ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 p-4">
          <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white p-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h4 className="text-base font-black text-slate-900">Promote To Gold Example</h4>
                <p className="text-[12px] text-slate-500">Create a supervised example from a real suggestion log or manual approved text.</p>
              </div>
              <button type="button" onClick={() => setPromoteOpen(false)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-slate-500">X</button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <input value={promoteDraft.sourceSuggestionLogId} onChange={(event) => setPromoteDraft({ ...promoteDraft, sourceSuggestionLogId: event.target.value })} placeholder="Source Suggestion Log ID (optional)" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <input value={promoteDraft.title} onChange={(event) => setPromoteDraft({ ...promoteDraft, title: event.target.value })} placeholder="Title" className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <select value={promoteDraft.scenario} onChange={(event) => setPromoteDraft({ ...promoteDraft, scenario: event.target.value as '' | Scenario })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                <option value="">Scenario (infer from log if possible)</option>
                {scenarioOptions.map(option => (
                  <option key={option} value={option}>{scenarioLabel(option)}</option>
                ))}
              </select>
              <select value={promoteDraft.tone} onChange={(event) => setPromoteDraft({ ...promoteDraft, tone: event.target.value as Tone })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                {toneOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <input value={promoteDraft.tagsText} onChange={(event) => setPromoteDraft({ ...promoteDraft, tagsText: event.target.value })} placeholder="Tags (comma separated)" className="md:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <textarea value={promoteDraft.inputSummary} onChange={(event) => setPromoteDraft({ ...promoteDraft, inputSummary: event.target.value })} placeholder="Input Summary (optional when promoting from log)" rows={2} className="md:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <input value={promoteDraft.approvedSubject} onChange={(event) => setPromoteDraft({ ...promoteDraft, approvedSubject: event.target.value })} placeholder="Approved Subject (optional)" className="md:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <textarea value={promoteDraft.approvedBody} onChange={(event) => setPromoteDraft({ ...promoteDraft, approvedBody: event.target.value })} placeholder="Approved Body (optional when source log has generated body)" rows={5} className="md:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <textarea value={promoteDraft.approvalNote} onChange={(event) => setPromoteDraft({ ...promoteDraft, approvalNote: event.target.value })} placeholder="Approval Note (optional)" rows={2} className="md:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setPromoteOpen(false)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600">Cancel</button>
              <button type="button" onClick={() => void submitPromote()} disabled={saving} className="rounded-lg bg-slate-900 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-60">{saving ? 'Promoting...' : 'Promote Example'}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
